import * as React from 'react';
import { Component } from 'react';
import { Field, Form, Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import { AppUser } from 'Src/views/App/App';
import { School } from '../../../../../models/school';
import { Request } from '../../../../../models/request';
import { Permission } from '../../../../../models/permission';
import { DEFAULT_LIMIT } from '../../../../../consts/table';
import { PERMISSION_REQUEST_STATUS } from '../../../../../consts/permissionRequest';
import { USER_GENDER, GENDER_SERVER_TO_CLIENT_MAPPING, ROLE } from '../../../../../consts/user';
import {
  getPostcode,
  searchFunctionSchools,
  getName,
  searchFunctionSchoolFormsPublic,
  searchFunctionSchoolHousesPublic,
  searchFunctionSchoolSports,
  searchFunctionSchoolTournaments
} from 'Src/helpers/autocomplete/autocomplete';
import { getPermissions } from 'Src/helpers/service/admin/user';
import { getSelectOptionForRolesRequest } from '../../../../../helpers/table/select';
import { Loader } from '../../../../../components/Loader/Loader';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import './RequestForm.scss';
import { getPostcodes } from '../../../../../helpers/venue/venue';

interface Props {
  user: AppUser;
  onCancel: () => void;
  onSubmit: (data: any) => void;
  requests: Request[];
}

const RequestLabel = (props: { text: string }) => <label className="upRequestLabel">{props.text}</label>;

interface State {
  postcode: string;
  school: School;
  sportIds: string[];
  isLoading: boolean;
  permissions: Permission[];
}

export class RequestForm extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      postcode: undefined,
      school: undefined,
      sportIds: [],
      permissions: []
    };
  }

  componentDidMount() {
    const { user } = this.props;
    this.setState({
      isLoading: true
    });

    const filter = {
      limit: DEFAULT_LIMIT,
      skip: 0
    };

    getPermissions(user, filter).then(permissions => {
      this.setState({
        permissions,
        isLoading: false
      });
    });
  }

  getSchools = (text: string) => {
    const { user } = this.props;
    const postcode = this.state.postcode;
    return searchFunctionSchools(user, text, postcode);
  };

  getRoleOptions = () => {
    const { requests } = this.props;
    const { permissions, school } = this.state;
    if (!school) return [];
    const newStatusRequests = requests.filter(request => request.status === PERMISSION_REQUEST_STATUS.NEW);
    const isFirstRequest = permissions.length === 0 && newStatusRequests.length === 0 ? true : false;

    return getSelectOptionForRolesRequest(school, permissions, isFirstRequest);
  };

  getSchoolForms = (text: string) => {
    const { school } = this.state;
    return searchFunctionSchoolFormsPublic(text, school.id);
  };

  getSchoolHouses = (text: string) => {
    const { school } = this.state;
    return searchFunctionSchoolHousesPublic(text, school.id);
  };

  getSchoolSports = (text: string) => {
    const { school, sportIds } = this.state;
    return searchFunctionSchoolSports(text, school.id, sportIds);
  };

  getSchoolTournaments = (text: string) => {
    const { school } = this.state;
    return searchFunctionSchoolTournaments(text, school.id);
  };

  render() {
    const { isLoading, school } = this.state;
    const { onCancel, onSubmit, user } = this.props;

    const isSchoolExist = typeof school !== 'undefined';

    if (isLoading) {
      return <Loader />;
    }

    const requestInitial = {
      postcode: undefined,
      comment: '',
      preset: '',
      school: undefined,
      schoolId: '',
      //parent fields
      studentFirstName: '',
      studentLastName: '',
      childDateOfBirth: undefined,
      childGender: USER_GENDER.MALE,
      childHouse: undefined,
      childHouseId: '',
      childForm: undefined,
      childFormId: '',
      //teacher and coach fields
      sportIds: [],
      sports: [],
      //tournament folllower fields
      tournament: undefined,
      tournamentId: ''
    };

    const requestSchema = Yup.object().shape({
      preset: Yup.string().required('Please select a role.'),
      schoolId: Yup.string().required('Please select a school.'),
      childDateOfBirth: Yup.date()
    });

    const isRoleCoachOrTeacher = (role: string) => {
      return role === ROLE.COACH || role === ROLE.TEACHER;
    };

    const isRoleParent = (role: string) => {
      return role === ROLE.PARENT;
    };

    const isRoleParentOrStudent = (role: string) => {
      return role === ROLE.PARENT || role === ROLE.STUDENT;
    };

    const isRoleTournamentFollower = (role: string) => {
      return role === ROLE.TOURNAMENT_FOLLOWER;
    };

    const isRoleGovernor = (role: string) => {
      return role === ROLE.GOVERNOR;
    };

    const isRoleAdminOrManager = (role: string) => {
      return role === ROLE.ADMIN || role === ROLE.MANAGER;
    };

    const isRoleStudent = (role: string) => {
      return role === ROLE.STUDENT;
    };

    const isEmpty = field => {
      return field === '' || typeof field === 'undefined';
    };

    return (
      <div>
        <Formik
          initialValues={requestInitial}
          validationSchema={requestSchema}
          onSubmit={values => {
            const dataCopy = { ...values };
            const { comment } = dataCopy;
            const isCommentExist = typeof comment !== 'undefined' && comment !== '';

            delete dataCopy.school;
            delete dataCopy.postcode;

            if (isRoleParent(values.preset)) {
              dataCopy.comment = `Request to be parent of [${dataCopy.studentFirstName} ${dataCopy.studentLastName}]`;
              delete dataCopy.childHouse;
              delete dataCopy.childForm;
              delete dataCopy.tournament;
              delete dataCopy.tournamentId;
              delete dataCopy.sports;
              delete dataCopy.sportIds;

              if (isEmpty(dataCopy.studentFirstName)) {
                delete dataCopy.studentFirstName;
              }

              if (isEmpty(dataCopy.studentLastName)) {
                delete dataCopy.studentLastName;
              }

              if (isEmpty(dataCopy.childDateOfBirth)) {
                delete dataCopy.childDateOfBirth;
              }

              if (isEmpty(dataCopy.childGender)) {
                delete dataCopy.childGender;
              }

              if (isEmpty(dataCopy.childHouseId)) {
                delete dataCopy.childHouseId;
              }

              if (isEmpty(dataCopy.childFormId)) {
                delete dataCopy.childFormId;
              }
            }

            if (isRoleCoachOrTeacher(values.preset)) {
              if (!isCommentExist) {
                delete dataCopy.comment;
              }
              delete dataCopy.studentFirstName;
              delete dataCopy.studentLastName;
              delete dataCopy.childDateOfBirth;
              delete dataCopy.childGender;
              delete dataCopy.childHouse;
              delete dataCopy.childHouseId;
              delete dataCopy.childForm;
              delete dataCopy.childFormId;
              delete dataCopy.tournament;
              delete dataCopy.tournamentId;
              delete dataCopy.sports;
            }

            if (isRoleTournamentFollower(values.preset)) {
              delete dataCopy.tournamentId;
              if (!isCommentExist) {
                delete dataCopy.comment;
              }
              delete dataCopy.studentFirstName;
              delete dataCopy.studentLastName;
              delete dataCopy.childDateOfBirth;
              delete dataCopy.childGender;
              delete dataCopy.childHouse;
              delete dataCopy.childHouseId;
              delete dataCopy.childForm;
              delete dataCopy.childFormId;
              delete dataCopy.sports;
              delete dataCopy.sportIds;
              delete dataCopy.tournament;
            }
            if (values.preset === ROLE.SLT || isRoleGovernor(values.preset) || isRoleAdminOrManager(values.preset) || isRoleStudent(values.preset)) {
              if (!isCommentExist) {
                delete dataCopy.comment;
              }
              delete dataCopy.studentFirstName;
              delete dataCopy.studentLastName;
              delete dataCopy.childDateOfBirth;
              delete dataCopy.childGender;
              delete dataCopy.childHouse;
              delete dataCopy.childHouseId;
              delete dataCopy.childForm;
              delete dataCopy.childFormId;
              delete dataCopy.sports;
              delete dataCopy.sportIds;
              delete dataCopy.tournament;
              delete dataCopy.tournamentId;
            }

            onSubmit(dataCopy);
          }}
        >
          {({ setFieldValue, values }) => (
            <Form className="upRequestForm">
              <div className="eRequestFormTitle">
                <span className="eRequestFormTitleIcon" aria-hidden="true">
                  +
                </span>
                <span>New Request</span>
              </div>

              <RequestLabel text="Postcode" />
              <div className="eRequestFormRowSection">
                <Field name="postcode">
                  {({ field }) => {
                    return (
                      <Autocomplete
                        searchFunction={text => getPostcodes(text, user)}
                        getElementTitle={getPostcode}
                        customClass="upRequestAutocomplete"
                        defaultItem={values.postcode}
                        onSelect={postcode => {
                          this.setState({ postcode, school: undefined });
                          setFieldValue('postcode', postcode);
                          setFieldValue('school', undefined);
                          setFieldValue('schoolId', '');
                          setFieldValue('preset', '');
                        }}
                        placeholder={'Please type your postcode'}
                        isMenuPortal={true}
                      />
                    );
                  }}
                </Field>
                <button
                  type="button"
                  className="upRequestIconButton bDeleteButton"
                  onClick={() => {
                    setFieldValue('postcode', undefined);
                    setFieldValue('school', undefined);
                    setFieldValue('schoolId', '');
                    setFieldValue('preset', '');
                    this.setState({ postcode: undefined, school: undefined });
                  }}
                >
                  X
                </button>
              </div>
              <RequestLabel text="School or union" />
              <Field name="schoolId">
                {({ field }) => {
                  return (
                    <Autocomplete
                      searchFunction={this.getSchools}
                      getElementTitle={getName}
                      customClass="upRequestAutocomplete"
                      defaultItem={values.school}
                      onSelect={school => {
                        this.setState({ school });
                        setFieldValue('school', school);
                        setFieldValue('schoolId', school.id);
                      }}
                      placeholder={'Search for your school or union'}
                      isItemWithImage={true}
                      isMenuPortal={true}
                      showAllOnFocus={true}
                      menuMaxHeight={360}
                      emptyMessage="No matching schools. Try a postcode or another part of the school name."
                    />
                  );
                }}
              </Field>
              <ErrorMessage component="div" className="alert alert-danger" name="schoolId" />

              <RequestLabel text="Role" />
              {isSchoolExist ? (
                <div className="upRequestChoiceChips" role="group" aria-label="Role">
                  {this.getRoleOptions().map(option => (
                    <button
                      key={option.value}
                      type="button"
                      className={
                        values.preset === option.value ? 'upRequestChoiceChip isActive' : 'upRequestChoiceChip'
                      }
                      aria-pressed={values.preset === option.value}
                      onClick={() => setFieldValue('preset', option.value)}
                    >
                      {option.text}
                    </button>
                  ))}
                </div>
              ) : (
                <Field
                  type="text"
                  name="preset"
                  disabled={true}
                  className="upRequestInput eAutocompleteInput"
                  placeholder={'Please select school first'}
                />
              )}
              <ErrorMessage component="div" className="alert alert-danger" name="preset" />

              {isRoleParent(values.preset) && (
                <>
                  <RequestLabel text="Student first name" />
                  <Field type="text" name="studentFirstName" className="upRequestInput" />

                  <RequestLabel text="Student last name" />
                  <Field type="text" name="studentLastName" className="upRequestInput" />

                  <RequestLabel text="Gender" />
                  <div className="upRequestChoiceChips" role="group" aria-label="Student gender">
                    {[USER_GENDER.MALE, USER_GENDER.FEMALE].map(gender => (
                      <button
                        key={gender}
                        type="button"
                        className={
                          values.childGender === gender ? 'upRequestChoiceChip isActive' : 'upRequestChoiceChip'
                        }
                        aria-pressed={values.childGender === gender}
                        onClick={() => setFieldValue('childGender', gender)}
                      >
                        {GENDER_SERVER_TO_CLIENT_MAPPING[gender]}
                      </button>
                    ))}
                  </div>

                  <Field name="childDateOfBirth">
                    {() => (
                      <div className="upRequestDateField">
                        <SitDatePicker
                          label="Date of birth"
                          mode="single"
                          value={values.childDateOfBirth || null}
                          onChange={childDateOfBirth => setFieldValue('childDateOfBirth', childDateOfBirth || '')}
                          placeholder="Select date of birth"
                          format="DD MMM YYYY"
                          max={new Date()}
                          centered
                          clearable
                        />
                      </div>
                    )}
                  </Field>

                  <RequestLabel text="Form" />
                  <Field name="childForm">
                    {({ field }) => {
                      return (
                        <Autocomplete
                          searchFunction={this.getSchoolForms}
                          getElementTitle={getName}
                          customClass="upRequestAutocomplete"
                          defaultItem={values.childForm}
                          isMenuPortal={true}
                          showAllOnFocus={true}
                          onSelect={form => {
                            setFieldValue('childFormId', form.id);
                            setFieldValue('childForm', form);
                          }}
                        />
                      );
                    }}
                  </Field>

                  <RequestLabel text="House" />
                  <Field name="childHouse">
                    {({ field }) => {
                      return (
                        <Autocomplete
                          searchFunction={this.getSchoolHouses}
                          getElementTitle={getName}
                          customClass="upRequestAutocomplete"
                          defaultItem={values.childHouse}
                          isMenuPortal={true}
                          showAllOnFocus={true}
                          onSelect={house => {
                            setFieldValue('childHouseId', house.id);
                            setFieldValue('childHouse', house);
                          }}
                        />
                      );
                    }}
                  </Field>
                </>
              )}

              {isRoleCoachOrTeacher(values.preset) && (
                <>
                  <RequestLabel text="Activities / Sports" />

                  {values.sports.length > 0 && (
                    <div className="upRequestSelectedChips">
                      {values.sports.map((sport, index) => (
                        <span className="upRequestSelectedChip" key={sport.id || `${sport.name}_${index}`}>
                          <span>{sport.name}</span>
                          <button
                            className="upRequestSelectedChipRemove"
                            type="button"
                            aria-label={`Remove ${sport.name}`}
                            onClick={() => {
                              const sportsUpdated = [...values.sports];
                              const sportIdsUpdated = [...values.sportIds];
                              sportsUpdated.splice(index, 1);
                              sportIdsUpdated.splice(index, 1);
                              setFieldValue('sports', sportsUpdated);
                              setFieldValue('sportIds', sportIdsUpdated);
                              this.setState({
                                sportIds: sportIdsUpdated
                              });
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <Field name="sports">
                    {({ field }) => {
                      return (
                        <Autocomplete
                          key={`request_sport_picker_${values.sports.length}`}
                          searchFunction={this.getSchoolSports}
                          getElementTitle={getName}
                          customClass="upRequestAutocomplete"
                          isMenuPortal={true}
                          menuMaxHeight={320}
                          onSelect={sport => {
                            const sportsUpdated = [...values.sports, sport];
                            const sportIdsUpdated = [...values.sportIds, sport.id];
                            setFieldValue('sportIds', sportIdsUpdated);
                            setFieldValue('sports', sportsUpdated);
                            this.setState({
                              sportIds: sportIdsUpdated
                            });
                          }}
                          placeholder={'Please select activities/sports'}
                        />
                      );
                    }}
                  </Field>
                </>
              )}

              {isRoleTournamentFollower(values.preset) && <p className="upRequestLabel">Follow the tournaments your union organises or takes part in. Only public tournament information is available; no child link is required.</p>}

              {isRoleParentOrStudent(values.preset) && values.school && values.school.isOneOffCodeOnRegisterEnabled && (
                <div className="form-group">
                  <RequestLabel text="If the school provided you with a unique code please enter it here" />
                  <Field name="oneOffCode" className={'upRequestInput'} />
                </div>
              )}

              <RequestLabel text="Comments" />
              <div>
                <Field component="textarea" name="comment" className="upRequestInput upRequestTextarea" />
              </div>

              <div className="upRequestFormActions">
                <button className="upProfileBtn upProfileBtnSecondary" type="button" onClick={onCancel}>
                  Cancel
                </button>
                <button type="submit" className="upProfileBtn upProfileBtnPrimary">
                  Submit
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}
